import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { t } from 'i18next';

import { cn } from 'src/lib/utils';

// tw-min-w-0 throughout: breadcrumbs now live in the fixed-height sticky header (#2611), where
// a trail that cannot shrink below its min-content width would push the page title — and then
// the header actions — off-screen. Every level opts into shrinking so the long crumbs
// ellipsize instead.
const Breadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'nav'> & {
    separator?: React.ReactNode;
  }
>(({ className, ...props }, ref) => (
  <nav ref={ref} aria-label="breadcrumb" className={cn('tw-min-w-0', className)} {...props} />
));
Breadcrumb.displayName = 'Breadcrumb';

const BreadcrumbList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<'ol'>>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        // tw-flex-nowrap, not tw-flex-wrap: a wrapping list line-breaks before it shrinks, so
        // the truncation below would never fire and the fixed-height header row would grow a
        // second line instead.
        'tw-flex tw-min-w-0 tw-flex-nowrap tw-items-center tw-gap-1.5 tw-break-words tw-text-muted-foreground sm:tw-gap-2.5',
        className,
      )}
      {...props}
    />
  ),
);
BreadcrumbList.displayName = 'BreadcrumbList';

const BreadcrumbItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      // Buttons and icons inside a crumb keep their size; only the text shrinks. A crumb whose
      // only child is a button has nothing shrinkable in it, so pass tw-shrink-0 at that call
      // site — otherwise min-w-0 lets the li collapse under its own button and the next crumb
      // paints on top of it.
      className={cn(
        'tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5 [&>button]:tw-shrink-0',
        className,
      )}
      {...props}
    />
  ),
);
BreadcrumbItem.displayName = 'BreadcrumbItem';

const BreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'> & {
    asChild?: boolean;
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref}
      className={cn(
        'tw-truncate tw-text-text-subtle tw-transition-colors hover:tw-text-foreground',
        className,
      )}
      {...props}
    />
  );
});
BreadcrumbLink.displayName = 'BreadcrumbLink';

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('tw-truncate tw-font-medium tw-text-text-strong', className)}
      {...props}
    />
  ),
);
BreadcrumbPage.displayName = 'BreadcrumbPage';

/**
 * The page's `h1`, rendered as a sibling of the breadcrumb `<nav>` rather than inside it — a
 * heading nested in a navigation landmark is announced as part of the trail instead of as the
 * page title. Styled to match `BreadcrumbPage` so a single-crumb page looks unchanged.
 *
 * The large visible page titles were removed in #2611, so this element carries the document
 * outline. Exactly one per page state.
 */
const BreadcrumbHeading = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<'h1'>
>(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn('tw-min-w-0 tw-truncate tw-font-medium tw-text-text-strong', className)}
    {...props}
  />
));
BreadcrumbHeading.displayName = 'BreadcrumbHeading';

/**
 * Separator between the breadcrumb `<nav>` and {@link BreadcrumbHeading}. `BreadcrumbSeparator`
 * is an `<li>` and belongs inside the list; this one is a span for use outside it.
 */
const BreadcrumbHeadingSeparator = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn('tw-shrink-0 tw-text-muted-foreground [&>svg]:tw-size-3.5', className)}
    {...props}
  >
    <ChevronRight />
  </span>
);
BreadcrumbHeadingSeparator.displayName = 'BreadcrumbHeadingSeparator';

/**
 * The header row is a single line that cannot wrap, so on narrow viewports a trail of ancestors
 * and the page's own title compete for ~255px and the title loses the most width. Ancestor crumbs
 * and the separators between them collapse below `md`; the current crumb and the `h1` stay, so the
 * header still says which page you are on.
 *
 * Exported so every trail collapses at the same breakpoint — these strings are the one place the
 * breakpoint is written down.
 */
const BREADCRUMB_ANCESTOR_COLLAPSED = 'tw-hidden md:tw-inline-flex';
const BREADCRUMB_SEPARATOR_COLLAPSED = 'tw-hidden md:tw-block';

const BreadcrumbSeparator = ({ children, className, ...props }: React.ComponentProps<'li'>) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('tw-shrink-0 [&>svg]:tw-size-3.5', className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
);
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator';

const BreadcrumbEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn('tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="tw-h-4 tw-w-4" />
    <span className="tw-sr-only">{t('actions:more', { defaultValue: 'More' })}</span>
  </span>
);
BreadcrumbEllipsis.displayName = 'BreadcrumbEllipsis';

export {
  BREADCRUMB_ANCESTOR_COLLAPSED,
  BREADCRUMB_SEPARATOR_COLLAPSED,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbHeading,
  BreadcrumbHeadingSeparator,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
